// IP fondi — `xsib-inventory` moduli bilan haqiqiy kontrakt.
// Manba (o'qilgan): `inventory/ippool/resource/IpPoolResource.java`,
// `domain/IpPool.java`, `domain/IpAllocation.java`, `dto/IpPoolDraft.java`.
//
// ⚠️ HAMMA IXTIYORIY MAYDONDA `.nullish()`, `.nullable()` EMAS —
// backendda `default-property-inclusion: non_null`, ya'ni `null`
// maydon JSON'ga umuman chiqmaydi. Sabab batafsil `crmApi.ts` da.
//
// ⚠️ IKKI XIL SO'ROV, IKKI XIL MAQSAD:
//   · `ip-pools`       — fondlarni BOSHQARISH (sozlash);
//   · `ip-allocations` — "shu manzilda KIM bor" degan savolga javob.
// Ikkinchisi kundalik ishda ko'proq kerak: shikoyat yoki so'rov IP
// bilan keladi, operatorga esa abonent kerak.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import type { TableState } from '@/shared/ui/table/tableState'

export const POOL_STATES = ['ACTIVE', 'DISABLED'] as const
export type PoolState = (typeof POOL_STATES)[number]

/** `inventory/equipment/domain/OwnerType.java` */
export const IP_OWNER_TYPES = ['SUBSCRIBER', 'NAS', 'VENUE', 'EQUIPMENT'] as const
export type IpOwnerType = (typeof IP_OWNER_TYPES)[number]

const poolSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema.nullish(),
  name: z.string(),
  cidr: z.string(),
  gateway: z.string().nullish(),
  vlanId: z.number().nullish(),
  state: z.string(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export type IpPool = z.infer<typeof poolSchema>

const allocationSchema = z.object({
  id: uuidSchema,
  poolId: uuidSchema.nullish(),
  ip: z.string(),
  ownerType: z.string(),
  ownerId: uuidSchema.nullish(),
  allocatedAt: instantSchema,
  /** `null` — manzil HOZIR band. Qiymat bo'lsa — bo'shatilgan */
  releasedAt: instantSchema.nullish(),
})

export type IpAllocation = z.infer<typeof allocationSchema>

/** URL filtri kaliti */
export const POOL_STATE_FILTER = 'state'

/** «Barcha holatlar» — filtrning tanlovi, fond holati EMAS */
export const POOL_STATE_ALL = 'ALL'

/**
 * Sukut ko'rinish — FAQAT AMALDAGI fondlar (egasi qarori 2026-08-20).
 *
 * ⚠️ O'chirilgan fond YO'QOLMAYDI: holat filtrida o'z tanlovi bor va
 * uni QAYTA YOQISH amali o'sha qatorda turadi. Bu yagona yo'l — fondni
 * o'chirish endpointi umuman yo'q.
 *
 * ⚠️ Filtr MIJOZDA va bu YOLG'ON EMAS: `GET /api/inventory/ip-pools`
 * birorta parametr qabul qilmaydi va TO'LIQ ro'yxat qaytaradi, jadvalda
 * sahifalash ham yo'q.
 */
export const POOL_DEFAULT_STATE: PoolState = 'ACTIVE'

/**
 * ⚠️ ATAYLAB AJRATILGAN funksiya: sukut qiymatni CHAQIRIB o'lchash
 * kerak (`tests/closed-default.mjs`). Noma'lum qiymat sukutga qaytadi.
 */
export function poolStateFilter(state: TableState): string {
  const selected = state.filters[POOL_STATE_FILTER]?.[0]
  if (selected === POOL_STATE_ALL) return POOL_STATE_ALL
  if (selected !== undefined && (POOL_STATES as readonly string[]).includes(selected)) {
    return selected
  }
  return POOL_DEFAULT_STATE
}

export async function fetchIpPools(): Promise<IpPool[]> {
  const response = await api.get<unknown>('/api/inventory/ip-pools')
  return z.array(poolSchema).parse(response.data)
}

export interface IpPoolDraft {
  name: string
  cidr: string
  gateway?: string | null
  vlanId?: number | null
}

export async function createIpPool(draft: IpPoolDraft): Promise<IpPool> {
  const response = await api.post<unknown>('/api/inventory/ip-pools', draft)
  return poolSchema.parse(response.data)
}

/**
 * ⚠️ CIDR bu yerda YO'Q va bu backend qarori
 * ({@code PoolDetailsUpdate}): faqat nom, shlyuz va VLAN o'zgaradi.
 *
 * Sabab ma'noli — CIDR o'zgarsa allaqachon berilgan manzillar fond
 * chegarasidan tashqarida qolib ketardi va ular "kimga tegishli"
 * degan savolga javobsiz bo'lardi. Fondni o'zgartirish kerak bo'lsa
 * yangisi ochiladi, eskisi o'chiriladi.
 */
export async function updateIpPool(
  id: string,
  body: { name: string; gateway?: string | null; vlanId?: number | null },
): Promise<IpPool> {
  const response = await api.put<unknown>(`/api/inventory/ip-pools/${id}`, body)
  return poolSchema.parse(response.data)
}

export async function setIpPoolState(id: string, state: PoolState): Promise<IpPool> {
  const response = await api.post<unknown>(`/api/inventory/ip-pools/${id}/state`, undefined, {
    params: { state },
  })
  return poolSchema.parse(response.data)
}

/**
 * Shu manzil HOZIR kimda.
 *
 * ⚠️ 404 — "band emas", XATO EMAS. Chaqiruvchi buni farqlashi shart:
 * "bo'sh manzil" va "so'rov yiqildi" butunlay boshqa javoblar va
 * ikkalasini bir xil ko'rsatish operatorni chalg'itardi.
 */
export async function fetchActiveByIp(ip: string): Promise<IpAllocation | null> {
  try {
    const response = await api.get<unknown>(`/api/inventory/ip-allocations/by-ip/${ip}`)
    return allocationSchema.parse(response.data)
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status
    if (status === 404) return null
    throw error
  }
}

/** Shu manzilning TARIXI — kim, qachon oldi va qachon bo'shatdi */
export async function fetchIpHistory(ip: string, limit = 100): Promise<IpAllocation[]> {
  const response = await api.get<unknown>(`/api/inventory/ip-allocations/by-ip/${ip}/history`, {
    params: { limit },
  })
  return z.array(allocationSchema).parse(response.data)
}

// --- Ajratish hayot davri --------------------------------------------------
//
// ⚠️ BU TO'RT CHAQIRUV BACKEND'DA ALLAQACHON BOR EDI, lekin ularni
// chaqiradigan birorta tugma yo'q edi. Ya'ni operator fond yarata olardi,
// undan manzil BERA OLMASDI va bo'shata ham olmasdi — modul yarim
// qurilgan holda turardi (`SubscriberFundAction.tsx` da xuddi shunday
// holat tasvirlangan).
//
// ⚠️ ENG MUHIMI — BU REYESTR `subscriber.static_ip` BILAN BOG'LANMAGAN.
// RADIUS'ga `Framed-IP-Address` ni abonent yozuvidagi `staticIp` beradi
// (`DefaultRadiusAuthorizeService`), bu yerdagi ajratma esa faqat
// INVENTAR hisobi: "shu manzil band, egasi falonchi". Server tomonda
// ularni bog'laydigan kod YO'Q va bu ataylab shunday (`xsib-inventory`
// `xsib-subscriber` ga murojaat qilmaydi). Demak panel ikkalasini bitta
// narsa qilib ko'rsatmasligi SHART, aks holda operator inventarda
// manzil ajratib, abonent esa eski IP bilan qolganini payqamaydi.

/**
 * Shu egaga HOZIR ajratilgan manzillar (bo'shatilganlar kirmaydi).
 *
 * ⚠️ Bo'sh ro'yxat — XATO EMAS: "bu abonentga fonddan manzil
 * ajratilmagan" degani. 404 kutmang, backend `[]` qaytaradi.
 */
export async function fetchAllocationsByOwner(
  ownerType: IpOwnerType,
  ownerId: string,
): Promise<IpAllocation[]> {
  const response = await api.get<unknown>('/api/inventory/ip-allocations/by-owner', {
    params: { ownerType, ownerId },
  })
  return z.array(allocationSchema).parse(response.data)
}

/**
 * Fonddagi birinchi BO'SH manzilni ajratadi.
 *
 * ⚠️ Fondda joy qolmasa backend 422 (`PoolExhausted`) qaytaradi — bu
 * texnik nosozlik emas, operator hal qiladigan holat (boshqa fond
 * tanlash yoki eski manzillarni bo'shatish).
 */
export async function allocateNextIp(
  poolId: string,
  ownerType: IpOwnerType,
  ownerId: string,
): Promise<IpAllocation> {
  const response = await api.post<unknown>(`/api/inventory/ip-pools/${poolId}/allocations/next`, {
    ownerType,
    ownerId,
  })
  return allocationSchema.parse(response.data)
}

/**
 * ANIQ manzilni ajratadi.
 *
 * ⚠️ Uch xil rad javobi bor va ular BOSHQA-BOSHQA: manzil shakli
 * noto'g'ri (400), fond oralig'idan tashqarida (422), allaqachon band
 * (409). `translateError` uchalasining matnini serverdan oladi, shuning
 * uchun bu yerda ularni birlashtirib "xato" deb ko'rsatish kerak emas.
 */
export async function allocateSpecificIp(
  poolId: string,
  ip: string,
  ownerType: IpOwnerType,
  ownerId: string,
): Promise<IpAllocation> {
  const response = await api.post<unknown>(`/api/inventory/ip-pools/${poolId}/allocations`, {
    ip,
    ownerType,
    ownerId,
  })
  return allocationSchema.parse(response.data)
}

/**
 * Manzilni bo'shatadi — qator O'CHIRILMAYDI, `releasedAt` qo'yiladi.
 *
 * ⚠️ Tarix saqlanadi va bu ataylab: "o'sha kuni bu IP kimda edi" degan
 * savol huquqiy murojaatlarda so'raladi (`IpPoolsPage` javadoc'i).
 */
export async function releaseIpAllocation(allocationId: string): Promise<IpAllocation> {
  const response = await api.post<unknown>(
    `/api/inventory/ip-allocations/${allocationId}/release`,
  )
  return allocationSchema.parse(response.data)
}
